/**
 * Unified V3 section-order panel — TREATX-V3-SECTION-ORDER-01.
 *
 * Reorders the HOMEPAGE SECTIONS (not the cards inside them). It writes the one
 * canonical field, `display_order`, on the V3-scoped record of each section, and
 * creates an order-only record for a section the owner has not adopted yet, so
 * nothing has to be adopted just to be moved.
 *
 * Numbers are SPARSE and directly editable: typing 19 over 30 drops a section
 * between 10 and 20 and leaves every other number exactly as it was. Only the
 * sections the owner actually moved are written.
 */
import React, { useMemo, useState } from 'react';
import { ArrowDown, ArrowUp, Eye, EyeOff, Loader2, Save } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import api from '../../utils/api';
import { V3_SECTIONS } from './v3SectionDefaults';
import {
  V3_ORDERABLE_SECTIONS,
  compareSections,
  effectiveSectionOrder,
} from '../../lib/v3SectionOrder';

const LABELS = Object.fromEntries(V3_SECTIONS.map((d) => [d.key, d.label]));

export const SectionOrderPanel = ({ records, onSaved }) => {
  // records: { [sectionKey]: cmsRecord | undefined }
  // Only the sections that are independently orderable: the hero is anchored,
  // the hero search panel lives inside it, and the Founding programme is nested
  // inside the provider section — none of the three can move on their own.
  const initial = useMemo(() => V3_ORDERABLE_SECTIONS
    .filter((k) => k in records)
    .map((key) => ({
      key,
      storedOrder: records[key]?.display_order,
      order: effectiveSectionOrder(key, records[key]?.display_order),
      managed: Boolean(records[key]),
      visible: (records[key]?.active ?? true) !== false,
    }))
    .sort(compareSections), [records]);

  const [rows, setRows] = useState(initial);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState('');
  const [error, setError] = useState('');

  const sorted = [...rows].sort((a, b) => a.order - b.order
    || initial.findIndex((r) => r.key === a.key) - initial.findIndex((r) => r.key === b.key));

  const setOrder = (key, value) => {
    setRows((rs) => rs.map((r) => (r.key === key ? { ...r, order: value } : r)));
    setError('');
  };

  // Move swaps the two numbers, so only those two sections are ever rewritten.
  const move = (key, delta) => {
    const list = sorted;
    const i = list.findIndex((r) => r.key === key);
    const j = i + delta;
    if (i < 0 || j < 0 || j >= list.length) return;
    const a = list[i];
    const b = list[j];
    setRows((rs) => rs.map((r) => {
      if (r.key === a.key) return { ...r, order: Number(b.order) };
      if (r.key === b.key) return { ...r, order: Number(a.order) };
      return r;
    }));
    setError('');
  };

  const save = async () => {
    const invalid = rows.find((r) => !Number.isInteger(Number(r.order))
      || Number(r.order) < 1 || Number(r.order) > 9999);
    if (invalid) {
      setError(`“${LABELS[invalid.key] || invalid.key}” needs a whole number between 1 and 9999.`);
      return;
    }
    setBusy(true);
    setError('');
    try {
      let written = 0;
      for (const row of rows) {
        const current = effectiveSectionOrder(row.key, row.storedOrder);
        const wanted = Number(row.order);
        // Write ONLY the sections the owner actually moved. A section left
        // alone keeps its number — and keeps having no record at all if it had
        // none — so nothing is renumbered or adopted as a side effect.
        if (wanted === current) continue;
        if (records[row.key]) {
          await api.put(`/admin/landing-content/${records[row.key].block_id}`,
            { display_order: wanted });
        } else {
          // Order-only record: no copy, so the section keeps rendering its
          // built-in text exactly as before.
          await api.post('/admin/landing-content', {
            block_type: 'section_copy',
            section_key: row.key,
            homepage_versions: ['V3'],
            active: true,
            display_order: wanted,
            title: { en: '' },
            description: { en: '' },
          });
        }
        written += 1;
      }
      setNote(written
        ? `Order saved — ${written} section${written === 1 ? '' : 's'} updated, the rest untouched.`
        : 'Nothing to save — this is already the saved order.');
      await onSaved();
      setTimeout(() => setNote(''), 5000);
    } catch (err) {
      const d = err?.response?.data?.detail;
      setError(typeof d === 'object' && d?.message ? d.message : 'Could not save the order.');
    } finally {
      setBusy(false);
    }
  };

  const dirty = rows.some((r) => Number(r.order)
    !== effectiveSectionOrder(r.key, r.storedOrder));

  return (
    <div className="rounded-lg border border-slate-200 bg-white p-3" data-testid="v3-order-panel">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <p className="text-sm font-semibold text-slate-900">Section order</p>
          <p className="mt-0.5 text-xs text-slate-500">
            The order of the SECTIONS down the page. Cards inside a section keep
            their own order, set in the section itself. Numbers are spaced in tens
            so you can type a number in between — 19 puts a section before the one
            at 20 — without renumbering anything else.
          </p>
        </div>
        <Button size="sm" onClick={save} disabled={busy || !dirty} data-testid="v3-order-save">
          {busy ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
                : <Save className="mr-1 h-3.5 w-3.5" />}
          {dirty ? 'Save order' : 'Order saved'}
        </Button>
      </div>

      {note && <p className="mt-2 rounded bg-emerald-50 px-2 py-1 text-xs text-emerald-700"
                  data-testid="v3-order-note">{note}</p>}
      {error && <p className="mt-2 rounded bg-rose-50 px-2 py-1 text-xs text-rose-700"
                   data-testid="v3-order-error">{error}</p>}

      <ol className="mt-3 divide-y divide-slate-100" data-testid="v3-order-list">
        <li className="flex items-center gap-2 py-1.5 text-xs text-slate-400">
          <span className="w-6 text-right">—</span>
          <span className="flex-1">Hero (always first, carries the search panel)</span>
        </li>
        {sorted.map((row, i) => (
          <li key={row.key} className="flex items-center gap-2 py-1.5"
              data-testid={`v3-order-row-${row.key}`}>
            <span className="w-6 text-right text-xs text-slate-400"
                  data-testid={`v3-order-position-${row.key}`}>{i + 1}</span>
            <span className="flex-1 text-xs font-medium text-slate-800">
              {LABELS[row.key] || row.key}
            </span>
            <span className={`rounded px-1.5 py-0.5 text-[10px] ${row.managed
              ? 'bg-sky-50 text-sky-700' : 'bg-slate-100 text-slate-500'}`}
                  data-testid={`v3-order-state-${row.key}`}>
              {row.managed ? 'Managed' : 'Built-in default'}
            </span>
            <span className="flex items-center gap-1 text-[10px] text-slate-500"
                  data-testid={`v3-order-visibility-${row.key}`}>
              {row.visible ? <Eye className="h-3 w-3" /> : <EyeOff className="h-3 w-3" />}
              {row.visible ? 'Shown' : 'Hidden'}
            </span>
            <Input type="number" min={1} max={9999} value={row.order}
                   onChange={(e) => setOrder(row.key, e.target.value)}
                   className="h-7 w-20 text-xs"
                   aria-label={`Position number for ${LABELS[row.key] || row.key}`}
                   data-testid={`v3-order-input-${row.key}`} />
            <Button size="icon" variant="outline" className="h-7 w-7"
                    disabled={i === 0} onClick={() => move(row.key, -1)}
                    aria-label={`Move ${LABELS[row.key] || row.key} up`}
                    data-testid={`v3-order-up-${row.key}`}>
              <ArrowUp className="h-3.5 w-3.5" />
            </Button>
            <Button size="icon" variant="outline" className="h-7 w-7"
                    disabled={i === sorted.length - 1} onClick={() => move(row.key, 1)}
                    aria-label={`Move ${LABELS[row.key] || row.key} down`}
                    data-testid={`v3-order-down-${row.key}`}>
              <ArrowDown className="h-3.5 w-3.5" />
            </Button>
          </li>
        ))}
      </ol>
    </div>
  );
};

export default SectionOrderPanel;
